import uniqueId from 'lodash/uniqueId'
import { StrictMode, useRef, useState } from 'react'
import type { VFC } from 'react'
import { Color, ZIndex } from '@opallabs/gem'
import { ThemeProvider } from '@opallabs/hooks'
import { Button, Icon, Popover, ToggleList } from '@opallabs/lapidary'
import * as styles from './styles'
import type { ViewOptionsPopoverProps } from './types'

export const ViewOptionsPopover: VFC<ViewOptionsPopoverProps> = ({
  onToggleViewOption,
  options = [],
  theme,
}) => {
  const areAnyChecked = Boolean(options?.find((option) => !!option?.value))
  const iconState = areAnyChecked ? styles.iconEnabled : styles.iconDisabled
  const popoverHtmlId = useRef<string>(uniqueId('view-options-popover'))
  const [open, setOpen] = useState<boolean>(false)

  return (
    <StrictMode>
      <ThemeProvider theme={theme}>
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger
            tooltip={{
              content: 'Content View Options',
              zIndex: ZIndex.Shroud.Tooltip,
            }}
          >
            <Button
              ariaControls={popoverHtmlId.current}
              ariaExpanded={open}
              ariaPressed={open}
              ariaLabel="Content View Options"
              variant="ghost"
              hideButtonText={true}
              icon={
                <Icon
                  ariaMode="hidden"
                  css={iconState}
                  glyph={'settings-lg'}
                  height={16}
                  width={16}
                  useHypernova={true}
                  color={Color[theme].Icon.Secondary}
                />
              }
              name="options-popover-trigger"
            />
          </Popover.Trigger>
          <Popover.Content
            style={{ zIndex: ZIndex.Shroud.Popover }}
            showArrow={true}
          >
            <ToggleList
              styleOverrides={styles.popoverStyles}
              toggles={options.map((toggle) => ({
                id: toggle?.name,
                displayName: toggle?.label,
                disabled: areAnyChecked && !toggle?.value,
                value: toggle?.value,
                leadingIcon: {
                  glyph: toggle?.icon,
                  color: Color.light.Surface.N600,
                  useHypernova: true,
                },
              }))}
              onToggleChange={(updatedToggle) => {
                onToggleViewOption({
                  name: updatedToggle.id,
                  value: updatedToggle.value,
                })
                setOpen(false)
              }}
            />
          </Popover.Content>
        </Popover.Root>
      </ThemeProvider>
    </StrictMode>
  )
}

ViewOptionsPopover.displayName = 'Photon: ViewOptionsPopover'
